(function () {
  const root = document.body;
  const doc = document.documentElement;
  const pageSearch = document.querySelector('.homepage-search');
  const pageSearchInner = pageSearch?.querySelector('.homepage-search__inner');
  const headerWrapper = document.querySelector('.header-wrapper');
  const header = document.querySelector('.header');
  const logo = document.querySelector('.header__heading-link');
  const icons = document.querySelector('.header__icons');
  const inlineSearchSlot = document.querySelector('.header__inline-search');
  const fieldInner = pageSearch?.querySelector('.page-search__field-inner');
  const announcementBar = document.querySelector('.announcement-bar-section');

  if (!pageSearch || !pageSearchInner || !header || !fieldInner || !headerWrapper) return;

  const MORPH_START = 20;
  const MORPH_DISTANCE = 140;
  const MOBILE_BREAKPOINT = 750;
  const LAYOUT_ON_THRESHOLD = 0.98;
  const LAYOUT_OFF_THRESHOLD = 0.02;
  const COLLAPSED_HEIGHT = {
    mobile: 24,
    desktop: 28,
  };

  let expandedBandHeight = 0;
  let expandedBandPaddingBottom = 0;
  let expandedAnchors = null;
  let collapsedAnchors = null;
  let headerLayoutActive = false;
  let ticking = false;
  let lastProgress = -1;
  let currentProgress = 0;

  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  function collapsedHeight() {
    return isMobile() ? COLLAPSED_HEIGHT.mobile : COLLAPSED_HEIGHT.desktop;
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function lerp(start, end, progress) {
    return start + (end - start) * progress;
  }

  function getScrollProgress(scrollY) {
    const rawProgress = Math.min(1, Math.max(0, (scrollY - MORPH_START) / MORPH_DISTANCE));
    return easeInOutCubic(rawProgress);
  }

  function isAtRest(scrollY, progress) {
    return progress <= 0 && scrollY <= MORPH_START;
  }

  function getWrapperRect() {
    return headerWrapper.getBoundingClientRect();
  }

  function getBandContentWidth() {
    const paddingLeft = parseFloat(getComputedStyle(pageSearch).paddingLeft) || 0;
    const paddingRight = parseFloat(getComputedStyle(pageSearch).paddingRight) || 0;
    return Math.max(0, pageSearch.clientWidth - paddingLeft - paddingRight);
  }

  function clearAbsoluteMorphStyles() {
    pageSearchInner.classList.remove('is-abs-morph');
    pageSearchInner.style.position = '';
    pageSearchInner.style.top = '';
    pageSearchInner.style.left = '';
    pageSearchInner.style.width = '';
    pageSearchInner.style.maxWidth = '';
    pageSearchInner.style.right = '';
    pageSearchInner.style.transform = '';
    pageSearch.classList.remove('is-morphing', 'is-morphed', 'is-docked');
  }

  function measureBandDimensions() {
    clearAbsoluteMorphStyles();
    expandedBandHeight = pageSearch.offsetHeight;
    expandedBandPaddingBottom = parseFloat(getComputedStyle(pageSearch).paddingBottom) || 0;
    doc.style.setProperty('--page-search-expanded-band-height', `${expandedBandHeight}px`);
    doc.style.setProperty('--page-search-band-padding', `${expandedBandPaddingBottom}px`);
  }

  function captureExpandedAnchors() {
    const wrapperRect = getWrapperRect();
    const innerRect = pageSearchInner.getBoundingClientRect();

    expandedAnchors = {
      top: innerRect.top - wrapperRect.top,
      left: innerRect.left - wrapperRect.left,
      width: innerRect.width || getBandContentWidth(),
    };
  }

  function getCollapsedAnchorsRelative(wrapperRect) {
    const height = collapsedHeight();

    if (headerLayoutActive && inlineSearchSlot) {
      const slotRect = inlineSearchSlot.getBoundingClientRect();

      if (slotRect.width > 48) {
        return {
          top: slotRect.top - wrapperRect.top + (slotRect.height - height) / 2,
          left: slotRect.left - wrapperRect.left,
          width: slotRect.width,
          height,
        };
      }
    }

    const headerRect = header.getBoundingClientRect();
    const logoRect = logo?.getBoundingClientRect() || headerRect;
    const iconsRect = icons?.getBoundingClientRect() || headerRect;
    const gap = isMobile() ? 8 : 12;
    const viewportTop = headerRect.top + (headerRect.height - height) / 2;
    const viewportLeft = logoRect.right + gap;
    const width = Math.max(0, iconsRect.left - viewportLeft - gap);

    return {
      top: viewportTop - wrapperRect.top,
      left: viewportLeft - wrapperRect.left,
      width,
      height,
    };
  }

  function refreshCollapsedAnchors() {
    collapsedAnchors = getCollapsedAnchorsRelative(getWrapperRect());
  }

  function enterMorphMode() {
    if (pageSearchInner.classList.contains('is-abs-morph')) return;
    captureExpandedAnchors();
    pageSearchInner.classList.add('is-abs-morph');
  }

  function exitMorphMode() {
    clearAbsoluteMorphStyles();
    expandedAnchors = null;
    measureBandDimensions();
  }

  function updateHeaderLayoutState(progress) {
    const wasActive = headerLayoutActive;

    if (progress >= LAYOUT_ON_THRESHOLD) {
      headerLayoutActive = true;
      if (!wasActive) {
        refreshCollapsedAnchors();
      }
    } else if (progress <= LAYOUT_OFF_THRESHOLD) {
      if (wasActive) {
        headerLayoutActive = false;
        refreshCollapsedAnchors();
        requestAnimationFrame(() => updateHeaderMetrics(currentProgress));
      }
    }

    root.classList.toggle('page-search-header-layout', headerLayoutActive);
  }

  function applyAbsoluteMorph(progress) {
    if (!expandedAnchors || !collapsedAnchors) return;

    const top = lerp(expandedAnchors.top, collapsedAnchors.top, progress);
    const left = lerp(expandedAnchors.left, collapsedAnchors.left, progress);
    const width = lerp(expandedAnchors.width, collapsedAnchors.width, progress);

    pageSearchInner.style.top = `${top}px`;
    pageSearchInner.style.left = `${left}px`;
    pageSearchInner.style.width = `${width}px`;
  }

  function updateHeaderMetrics(progress) {
    const headerRowHeight = header.offsetHeight;
    const announcementHeight = announcementBar?.offsetHeight || 0;
    const bandBaseHeight =
      expandedBandHeight ||
      parseFloat(getComputedStyle(doc).getPropertyValue('--page-search-expanded-band-height')) ||
      pageSearch.offsetHeight;
    const bandContribution = bandBaseHeight * (1 - progress);
    const groupHeight = announcementHeight + headerRowHeight + bandContribution;

    doc.style.setProperty('--header-height', `${headerRowHeight}px`);
    doc.style.setProperty('--announcement-bar-height', `${announcementHeight}px`);
    doc.style.setProperty('--header-search-band-height', `${bandContribution}px`);
    doc.style.setProperty('--header-group-offset', `${groupHeight}px`);
    doc.style.setProperty('--header-group-visible-height', `${groupHeight}px`);
    root.style.setProperty('--page-search-offset', `${groupHeight}px`);
  }

  function setProgressVars(progress) {
    const progressValue = progress.toFixed(4);
    doc.style.setProperty('--search-morph-progress', progressValue);
    doc.style.setProperty('--search-collapse-progress', progressValue);
    root.style.setProperty('--search-morph-progress', progressValue);
    root.style.setProperty('--search-collapse-progress', progressValue);
  }

  function updateSearchMode() {
    const scrollY = window.scrollY;
    const progress = getScrollProgress(scrollY);
    const isFullyScrolled = progress >= LAYOUT_ON_THRESHOLD;
    const isMorphing = progress > 0;
    const atRest = isAtRest(scrollY, progress);

    currentProgress = progress;

    updateHeaderLayoutState(progress);
    setProgressVars(progress);

    root.classList.toggle('page-search-morphing', isMorphing && progress < LAYOUT_ON_THRESHOLD);
    root.classList.toggle('page-search-scrolled', isFullyScrolled);
    root.classList.toggle('homepage-search-scrolled', isFullyScrolled);

    pageSearch.classList.toggle('is-morphing', isMorphing);
    pageSearch.classList.toggle('is-morphed', isFullyScrolled);
    pageSearch.classList.toggle('is-docked', isFullyScrolled);

    if (atRest) {
      if (pageSearchInner.classList.contains('is-abs-morph')) {
        exitMorphMode();
      }
      refreshCollapsedAnchors();
    } else {
      enterMorphMode();
      refreshCollapsedAnchors();
      applyAbsoluteMorph(progress);
    }

    const shouldUpdateMetrics =
      isMorphing || headerLayoutActive || Math.abs(progress - lastProgress) > 0.001 || atRest;

    if (shouldUpdateMetrics) {
      updateHeaderMetrics(progress);
      lastProgress = progress;
    }

    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(updateSearchMode);
      ticking = true;
    }
  }

  function onResize() {
    if (isAtRest(window.scrollY, currentProgress)) {
      measureBandDimensions();
    } else {
      captureExpandedAnchors();
      refreshCollapsedAnchors();
      applyAbsoluteMorph(currentProgress);
    }
    onScroll();
    updateHeaderMetrics(currentProgress);
  }

  let layoutObserver;

  function observeLayout() {
    if (!layoutObserver) {
      layoutObserver = new ResizeObserver(() => {
        if (isAtRest(window.scrollY, currentProgress)) {
          measureBandDimensions();
        } else {
          captureExpandedAnchors();
          refreshCollapsedAnchors();
        }
        updateHeaderMetrics(currentProgress);
        onScroll();
      });
    }

    [
      document.querySelector('.livdon-header-sticky-group'),
      headerWrapper,
      header,
      pageSearch,
      announcementBar,
    ]
      .filter(Boolean)
      .forEach((element) => {
        if (element.dataset.livdonLayoutObserved === 'true') return;
        element.dataset.livdonLayoutObserved = 'true';
        layoutObserver.observe(element);
      });
  }

  document.addEventListener('livdon:header-group-ready', () => {
    observeLayout();
    measureBandDimensions();
    updateSearchMode();
    updateHeaderMetrics(0);
  });

  document.addEventListener('livdon:announcement-height-change', () => {
    updateHeaderMetrics(currentProgress);
    onScroll();
  });

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  const init = () => {
    measureBandDimensions();
    refreshCollapsedAnchors();
    updateSearchMode();
    updateHeaderMetrics(0);
    observeLayout();
  };

  if (document.fonts?.ready) {
    document.fonts.ready.then(init);
  } else {
    init();
  }
})();
